import React from 'react';
import styled from 'styled-components';
import {ImageCompare, ImageCompareProps} from '@epic-mw/react-image-compare';
import {GridContainer} from '../grid';
import { AccentColorOptions, useAccentColor, useBreakpointDown } from 'epic-ue-theme';
import { CmsButtonProps } from '../buttons';
import { Grid } from '@material-ui/core';
import { AnimateOnViewWrap } from '../../wraps';
import { ImageAndTextCardContent } from '../cards/ImageAndTextCard.content';
import {Image} from '../images';
import { CompareSlider } from './CompareSlider';
import { ImageCompareTextCardLabel } from './ImageCompareTextCard.label';

const Styled = styled.div`
    svg {
        font-size: 7.3em;
        ${(p) => p.theme.breakpoints.down('sm')} {
            font-size: 5.7em;
        }
    }
    .paragraph {
        letter-spacing: 0.2px;
    }
    .ictc-image-wrap {
        position: relative;
        aspect-ratio: 16 / 9;
        object-fit: cover;
        overflow: hidden;
    }
    .ue-link-wrap {
        img {
            transition: transform 0.2s ${(p) => p.theme.easing};
            &:hover {
                transform: scale3d(1.01, 1.01, 1.01);
            }
        }
    }

    .ue-image-compare-text-card-img {
        //Always align image with top eyebrow on MD
        ${(p) => p.theme.breakpoints.only('md')} {
            align-self: baseline;
        }
    }
`;

export interface ImageCompareTextCardProps extends ImageCompareProps {
    hover?: boolean | false;
    heading?: string;
    title?: string;
    eyebrow?: string;
    description?: string;
    accentColor?: AccentColorOptions;
    button?: CmsButtonProps;
    alignment?: 'left' | 'right';
    noGridContainer?: boolean;
    sliderAccentColor?: AccentColorOptions;
    leftImageLabel?: string;
    rightImageLabel?: string;
}

export const ImageCompareTextCard = ({
    heading,
    title,
    leftImage,
    rightImage,
    accentColor = 'sky',
    alignment = 'left',
    noGridContainer,
    sliderAccentColor = 'forest',
    leftImageLabel,
    rightImageLabel,
    ...rest
}: ImageCompareTextCardProps): JSX.Element => {

    const isSm = useBreakpointDown('sm');
    const sliderLineColor = useAccentColor(sliderAccentColor);
    const leftLabel = leftImageLabel ? <ImageCompareTextCardLabel label={leftImageLabel} type="left" /> : undefined;
    const rightLabel = rightImageLabel ? <ImageCompareTextCardLabel label={rightImageLabel} type="right" /> : undefined;

    const imageElem = (
        <Grid item xs={12} sm={12} md={8} lg={8} xl={8} className="ue-image-compare-text-card-img">
            <div className="ictc-image-wrap">
                {leftImage && rightImage ?
                    <ImageCompare
                        sliderLineWidth={3}
                        sliderLineColor={sliderLineColor}
                        handle={<CompareSlider />}
                        leftImage={leftImage || ''}
                        rightImage={rightImage || ''}
                        leftImageLabel={leftLabel}
                        rightImageLabel={rightLabel}
                        sliderPositionPercentage={0.6}
                        {...rest} />
                    :
                    <Image src={leftImage || rightImage} fit="cover" />
                }
            </div>
        </Grid>
    );

    const contentElem = (
        <ImageAndTextCardContent
            {...rest}
            heading={heading}
            title={title}
            accentColor={accentColor}
            radiantEyebrow />
    );

    let aligmentElems: JSX.Element = <div className="ue-image-compare-text-card-no-alignment" />;

    const right = alignment === 'right' && !isSm;
    if (right) {
        aligmentElems = (
            <>
                {imageElem}
                {contentElem}
            </>
        );
    } else {
        aligmentElems = (
            <>
                {contentElem}
                {imageElem}
            </>
        );
    }

    const id = heading || title;

    const el: JSX.Element = (
        <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
            className={`ictc-grid ${right ? 'right' : 'left'}`}
            spacing={isSm ? 0 : 8}
            key={`ictc-grid-${id}`}>
            {aligmentElems}
        </Grid>
    );

    return (
        <AnimateOnViewWrap translateY={60}>
            <Styled
                className="ue-image-compare-text-card"
                key={`ictc-${id}`}>
                {noGridContainer ? el : <GridContainer>{el}</GridContainer>}
            </Styled>
        </AnimateOnViewWrap>
    );
};
